/* eslint-disable react-hooks/exhaustive-deps */
import {
  EmailRounded,
  HomeRounded,
  PhoneRounded,
  PrintRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import assets from "assets";
import UNIVERSAL from "config";
import useBase from "hooks/useBase";
import useFormatter from "hooks/useFormatter";
import moment from "moment";
import { useEffect, useRef } from "react";
import ReactLoading from "react-loading";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import { numberToWords } from "utils/numberToWord";

const Profile = ({ profile, loading }) => {
  const siteInfo = useSelector((state) => state?.base?.site_info);
  const { formatCurrency } = useFormatter();
  const {
    base: { settings },
    updateBase,
  } = useBase();

  useEffect(() => {
    updateBase(["settings"]);
  }, []);

  const columns = [
    { id: "item", label: "Category", minWidth: 100 },
    { id: "subtotal", label: "Subtotal", minWidth: 100 },
    { id: "tax", label: "Tax", minWidth: 100 },
    { id: "discount", label: "Discount", minWidth: 100 },
    { id: "total", label: "Total" },
  ];
  const payColumns = [
    // { id: "transaction_id", label: "TrxID" },
    { id: "payment_method_name", label: "Pay Method" },
    { id: "payment_date", label: "Pay Date" },
    { id: "note", label: "Note", minWidth: 100 },
    { id: "amount", label: "Amount" },
  ];
  const rows = profile?.invoice_details || [];
  const payRows = profile?.invoice_payments || [];
  const symbol = profile?.symbol;

  const printableContentRef = useRef();

  // Check if there's any tax or discount in the invoice items
  const hasTax = rows.some((row) => row.tax && parseFloat(row.tax) > 0);
  const hasDiscount = rows.some(
    (row) => row.discount && parseFloat(row.discount) > 0
  );

  // Check if there's any note in the payment history
  const hasNote = payRows.some((row) => row.note && row.note.trim() !== "");

  // Filter columns based on tax and discount presence
  const visibleColumns = columns.filter((col) => {
    if (col.id === "tax" && !hasTax) return false;
    if (col.id === "discount" && !hasDiscount) return false;
    return true;
  });

  // Filter payment columns based on note presence
  const visiblePayColumns = payColumns.filter((col) => {
    if (col.id === "note" && !hasNote) return false;
    return true;
  });

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    rows.forEach((row) => {
      subtotal += parseFloat(row.subtotal || 0);
      totalTax += parseFloat(row.tax || 0);
      totalDiscount += parseFloat(row.discount || 0);
    });

    return {
      subtotal,
      tax: totalTax,
      discount: totalDiscount,
      total: profile?.total || 0,
    };
  };

  const totals = calculateTotals();

  return loading ? (
    <Box
      sx={{
        height: "calc(100vh - 162px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ReactLoading
        type="cylon"
        color="#405189"
        height={"85px"}
        width={"100px"}
      />
    </Box>
  ) : (
    <Box sx={{ padding: "25px" }}>
      <Box
        sx={{
          mb: "15px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <ReactToPrint
          documentTitle={profile?.invoice_number}
          trigger={() => (
            <Button variant="outlined" startIcon={<PrintRounded />}>
              Print
            </Button>
          )}
          content={() => printableContentRef.current}
        />
      </Box>

      <Box
        ref={printableContentRef}
        className="printable-content"
        sx={{
          position: "relative",
          minHeight: "100vh", // Ensure at least full page height
          display: "flex",
          flexDirection: "column",
          "@media print": {
            minHeight: "calc(100vh - 10mm)", // A4 height minus margins
            height: "auto",
            margin: "10mm 10mm 0",
          },
        }}
      >
        {/* Watermark */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            opacity: 0.05,
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <img
            src={assets.svLogo}
            alt="Watermark"
            style={{
              width: "800px",
              height: "auto",
            }}
            className="watermark"
          />
        </Box>

        {/* Header Section - Company Logo and Details - Always at Top */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            // mb: "35px",
            width: "100%",
            position: "relative",
            zIndex: 1,
            "@media print": {
              position: "relative",
              top: 0,
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <img
              src={UNIVERSAL.BASEURL + siteInfo?.site_logo}
              alt=""
              style={{
                // maxHeight: "35px",
                height: "fit-content",
                maxWidth: "240px",
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                fontSize={12}
                fontWeight={600}
                color="#666"
                pl="3px"
              >
                Member of
              </Typography>
              <img
                src={assets.basisLogo}
                alt="BASIS Member"
                style={{
                  maxHeight: "60px",
                  maxWidth: "120px",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Invoice Title */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            mb: 3,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Typography variant="h4" fontWeight={600}>
            Invoice
          </Typography>
        </Box>

        {/* Middle Content Section - Flex-Grow to Fill Space */}
        <Box sx={{ flexGrow: 1, position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 40%",
              gap: "30px",
            }}
          >
            <Box>
              {/* <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr" }}>
                <Typography
                  textTransform="uppercase"
                  sx={{ fontWeight: "500" }}
                >
                  Branch:
                </Typography>
                <Typography sx={{ fontWeight: "500" }}>
                  {" "}
                  {profile?.branch_name || "-"}
                </Typography>
              </Box> */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "160px 1fr",
                  "@media print": { gridTemplateColumns: "120px 1fr" },
                }}
              >
                <Typography textTransform="capitalize">
                  Customer Name:
                </Typography>
                <Typography fontWeight={500}>
                  {" "}
                  {profile?.customer_name}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "160px 1fr",
                  "@media print": { gridTemplateColumns: "120px 1fr" },
                }}
              >
                <Typography textTransform="capitalize">
                  Customer Phone:
                </Typography>
                <Typography fontWeight={500}>
                  {" "}
                  {profile?.customer_phone}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "160px 1fr",
                  "@media print": { gridTemplateColumns: "120px 1fr" },
                }}
              >
                <Typography textTransform="capitalize">
                  Customer Address:
                </Typography>
                <Typography
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                  fontWeight={500}
                >
                  {" "}
                  {profile?.customer_address}
                </Typography>
              </Box>
              {/* <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr" }}>
                <Typography textTransform="capitalize">Project Name:</Typography>
                <Typography>
                  {profile?.project_name ? profile.project_name : "-"}
                </Typography>
              </Box> */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "160px 1fr",
                  "@media print": { gridTemplateColumns: "120px 1fr" },
                }}
              >
                <Typography textTransform="capitalize">
                  Payment Status:
                </Typography>
                <Typography textTransform="capitalize" fontWeight={500}>
                  {profile?.status}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr" }}>
                <Typography textTransform="capitalize">
                  Invoice Number:
                </Typography>
                <Typography fontWeight={500}>
                  {profile?.invoice_number}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr" }}>
                <Typography textTransform="capitalize">
                  Invoice Date:
                </Typography>
                <Typography fontWeight={500}>
                  {moment(profile?.created_at).format("ll")}
                </Typography>
              </Box>
              {profile?.status?.toLowerCase() !== "paid" && (
                <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr" }}>
                  <Typography textTransform="capitalize">Due Date:</Typography>
                  <Typography
                    fontWeight={500}
                    sx={{
                      color:
                        moment(profile?.due_date).format("YYYY-MM-DD") <
                        moment().format("YYYY-MM-DD")
                          ? "red"
                          : "#222",
                    }}
                  >
                    {profile?.due_date
                      ? moment(profile?.due_date).format("ll")
                      : "-"}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr" }}>
                <Typography textTransform="capitalize">Paid Amount:</Typography>
                <Typography textTransform="capitalize" fontWeight={500}>
                  {profile?.paid_amount
                    ? `${symbol}${profile.paid_amount}`
                    : "-"}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr" }}>
                <Typography textTransform="capitalize">Due Amount:</Typography>
                <Typography textTransform="capitalize" fontWeight={500}>
                  {profile?.paid_amount
                    ? `${profile?.symbol}${
                        profile?.total - profile?.paid_amount
                      }`
                    : profile?.symbol + profile?.total}
                </Typography>
              </Box>
            </Box>
          </Box>
          <TableContainer
            sx={{
              mt: "20px",
              border: "1px solid rgba(224, 224, 224, 1)",
              "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root":
                {
                  borderBottom: "none",
                },
            }}
          >
            <Table stickyHeader>
              <TableBody>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableCell
                      sx={{ fontWeight: "500" }}
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
                {rows?.map((row) => {
                  return (
                    <TableRow hover role="checkbox" key={row.id}>
                      {visibleColumns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.id === "item" ? (
                              row?.refund_type ? (
                                `${value} (${row.refund_type})`
                              ) : (
                                value
                              )
                            ) : column.id === "tax" ? (
                              <Typography variant="body2">
                                {"+ " + formatCurrency(value)}
                              </Typography>
                            ) : column.id === "discount" ? (
                              <Typography variant="body2">
                                {"- " + formatCurrency(value)}
                              </Typography>
                            ) : column.id === "total" ? (
                              <Typography variant="body2">
                                {formatCurrency(value)}
                              </Typography>
                            ) : column.id === "subtotal" ? (
                              <Typography variant="body2">
                                {formatCurrency(value)}
                              </Typography>
                            ) : (
                              value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography
            sx={{
              // mt: "25px",
              pt: "35px",
              pb: "10px",
              borderBottom: "1px solid #efefef",
            }}
          >
            Payments History
          </Typography>
          {profile?.invoice_payments?.length > 0 ? (
            <TableContainer
              sx={{
                // mt: "20px",
                border: "1px solid rgba(224, 224, 224, 1)",
                "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root":
                  {
                    borderBottom: "none",
                  },
              }}
            >
              <Table stickyHeader>
                <TableBody>
                  <TableRow>
                    {visiblePayColumns.map((column) => (
                      <TableCell
                        sx={{ fontWeight: "500" }}
                        key={column.id}
                        align={column.align}
                        style={{ minWidth: column.minWidth }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                  {payRows?.map((row) => {
                    return (
                      <TableRow hover role="checkbox" key={row.id}>
                        {visiblePayColumns.map((column) => {
                          const value = row[column.id];
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.id === "fee" ? (
                                value.name
                              ) : column.id === "amount" ? (
                                <Typography variant="body2">
                                  {formatCurrency(value)}
                                </Typography>
                              ) : (
                                value
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                py: "40px",
                // background: "#fbfbfb",
                mt: "10px",
              }}
            >
              No payments happened yet!
            </Typography>
          )}
          <Box
            sx={{
              margin: "20px 0 0 auto",
              padding: "20px 30px",
              border: "1px solid #caddff",
              width: "350px",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "120px auto",
                gap: "8px",
              }}
            >
              <Typography align="right" fontSize="15px">
                Subtotal
              </Typography>
              <Typography align="right" fontSize="15px">
                {formatCurrency(totals.subtotal) + "/-"}
              </Typography>

              {totals?.tax?.toFixed(2) > 0 && (
                <>
                  <Typography
                    align="right"
                    fontSize="15px"
                    sx={{ borderTop: "1px solid #ddd", pt: "8px" }}
                  >
                    Tax
                  </Typography>
                  <Typography
                    align="right"
                    fontSize="15px"
                    sx={{ borderTop: "1px solid #ddd", pt: "8px" }}
                  >
                    {formatCurrency(totals.tax) + "/-"}
                  </Typography>
                </>
              )}

              <Typography
                align="right"
                fontSize="16px"
                fontWeight={600}
                sx={{ borderTop: "1px solid #333", pt: "8px" }}
              >
                Total
              </Typography>
              <Typography
                align="right"
                fontSize="16px"
                fontWeight={600}
                sx={{ borderTop: "1px solid #333", pt: "8px" }}
              >
                {formatCurrency(totals.total) + "/-"}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px solid #ddd",
                fontWeight: 500,
                textAlign: "right",
              }}
            >
              In Words:{" "}
              {numberToWords(Math.floor(totals.total), profile?.currency_name)}
            </Typography>
          </Box>

          <Box sx={{ mt: 5 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Thank You!
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Payment Information:
            </Typography>
            <Box>
              <Typography variant="body2">
                <strong>Bank Name:</strong> Brac Bank Limited
              </Typography>
              <Typography variant="body2">
                <strong>Account Name:</strong> Soft Valley
              </Typography>
              <Typography variant="body2">
                <strong>Account Number:</strong> 2054973720001
              </Typography>
              <Typography variant="body2">
                <strong>Branch:</strong> Banani 11
              </Typography>
              <Typography variant="body2">
                <strong>Routing Number:</strong> 060260680
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Footer Section - Always at Bottom */}
        <Box
          sx={{
            // borderTop: "1px solid #efefef",
            pt: 1,
            mt: "auto",
            "@media print": {
              marginBottom: 0,
              position: "relative",
              bottom: 0,
              pageBreakBefore: "avoid",
              pageBreakInside: "avoid",
            },
          }}
        >
          {/* {settings?.app?.terms_and_conditions && (
            <>
              <Typography variant="subtitle2" fontWeight={500} gutterBottom>
                Terms & Conditions:
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                dangerouslySetInnerHTML={{
                  __html: settings?.app?.terms_and_conditions,
                }}
                sx={{
                  "& *": {
                    margin: 0,
                  },
                }}
              ></Typography>
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 7 }}>
            <Typography
              variant="body2"
              fontWeight={500}
              gutterBottom
              sx={{ borderTop: "1px solid #bbb", pt: 1 }}
            >
              Customer Signature
            </Typography>

            <Typography
              variant="body2"
              fontWeight={500}
              gutterBottom
              textAlign="right"
              sx={{ borderTop: "1px solid #bbb", pt: 1 }}
            >
              Approved By
            </Typography>
          </Box> */}

          <Box
            sx={{
              mt: 4,
              pt: 2,
              pb: 2,
              borderTop: "1px solid #efefef",
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#ff6b35",
                  borderRadius: "50%",
                  width: 30,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <HomeRounded sx={{ color: "#fff", fontSize: 16 }} />
              </Box>
              <Typography variant="body2" sx={{ maxWidth: 200 }}>
                {siteInfo?.site_address}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#333",
                  borderRadius: "50%",
                  width: 30,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EmailRounded sx={{ color: "#fff", fontSize: 16 }} />
              </Box>
              <Box>
                <Typography variant="body2">
                  {siteInfo?.email_address}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#2196f3",
                  borderRadius: "50%",
                  width: 30,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PhoneRounded sx={{ color: "#fff", fontSize: 16 }} />
              </Box>
              <Box>
                <Typography variant="body2">{siteInfo?.phone}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <style>
        {`
          @media print {
            @page {
              size: A4;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .printable-content {
              page-break-after: avoid;
              overflow: hidden;
            }
            .watermark {
              width: 80vw !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default Profile;
